import {useState, type ReactNode} from 'react';
import {Search, SlidersHorizontal, Calendar, Flag, FlagOff} from 'lucide-react';
import {
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {CUSTOMER_DECISIONS} from '@/lib/customerFeedback';
import {PLATFORM_ID} from '@/lib/platform';
import {FlagIcon, useFlagLabel} from '@/components/flags/Flag';
import {type FlagColor, FLAG_COLORS} from '@/api/flags';
import {FilterChip} from '@/components/shared/FilterChip';
import {
  MultiSelectFilterBody,
  stopMenuTypeahead,
  MenuClearButton,
  type MultiSelectOption
} from '@/components/shared/MultiSelectFilterBody';
import {checkState, toggleNode, type RiskTree, type RiskTreeNode} from '@/lib/riskTree';

export type {MultiSelectOption} from '@/components/shared/MultiSelectFilterBody';

// Local calendar date, not toISOString(): the UTC date is a day off for anyone west of Greenwich
// in the evening, and this feeds an <input type="date"> the analyst reads back directly.
export function isoDateDaysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export const STATUS_OPTIONS: MultiSelectOption[] = [
  {value: 'in hopper', label: 'In Hopper'},
  {value: 'pending qc review', label: 'Pending QC Review'},
  {value: 'held for p0', label: 'Held for P0'},
  {value: 'sent to client', label: 'Sent to Client'},
  {value: 'delivery failed', label: 'Delivery Failed'},
  {value: 'not sent - removed', label: 'Not Sent — Removed'},
  {value: 'none', label: 'No Action Status'}
];

export const TYPE_OPTIONS: MultiSelectOption[] = [
  {value: 'Actor', label: 'Actor'},
  {value: 'Content', label: 'Content'}
];

// 'not_checked' is its own value, distinct from 'unknown' - the same line PlatformStatusCell draws
// on the grid itself (never checked vs. checked and unreadable), so filtering has to offer both.
export const PLATFORM_STATUS_OPTIONS: MultiSelectOption[] = [
  {value: 'available', label: 'Available'},
  {value: 'removed', label: 'Removed'},
  {value: 'restricted', label: 'Restricted'},
  {value: 'unknown', label: 'Unknown'},
  {value: 'not_checked', label: 'Not Checked'}
];

// Whether the customer's feedback file covered a submission - being in it means they attributed
// the action to us - and if so, their decision as they gave it, in this deployment's customer's
// terms (Approve / No violation being a submission they judged a false positive).
export const CUSTOMER_FEEDBACK_OPTIONS: MultiSelectOption[] = [
  {value: 'any', label: 'Has feedback'},
  ...(PLATFORM_ID === 'pinterest' ? CUSTOMER_DECISIONS.pinterest : CUSTOMER_DECISIONS.youtube),
  {value: 'none', label: 'No feedback'}
];

// The analyst's own flags, plus 'none' for "rows I haven't flagged yet" - the one that makes
// working through a caseload and flagging as you go practical.
export const flagFilterOptions = (labelOf: (color: FlagColor) => string): MultiSelectOption[] => [
  ...FLAG_COLORS.map((color) => ({value: color, label: `${labelOf(color)} flag`})),
  {value: 'none', label: 'Unflagged'}
];

// The taxonomy as an indented checkbox tree. Selection is by leaf; ticking a branch ticks everything
// under it, and a partly ticked branch reads as indeterminate.
function RiskTreeFilterBody({
  tree,
  selected,
  onChange
}: {
  readonly tree: RiskTree;
  readonly selected: string[];
  readonly onChange: (next: string[]) => void;
}) {
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();
  const selectedSet = new Set(selected);

  // With a search term a node stays if its own path or any descendant's matches, so a hit keeps the
  // branch above it.
  function visible(node: RiskTreeNode): boolean {
    return !term || node.path.toLowerCase().includes(term) || node.children.some(visible);
  }

  function rows(nodes: RiskTreeNode[], depth: number): ReactNode[] {
    return nodes.filter(visible).flatMap((node) => {
      const state = checkState(node, selectedSet);

      return [
        <DropdownMenuCheckboxItem
          key={node.id}
          checked={state === 'checked'}
          className={INDENT_CLASSES[Math.min(depth, INDENT_CLASSES.length - 1)]}
          onCheckedChange={() => {
            onChange(toggleNode(node, selected));
          }}>
          <span className="truncate">{node.label}</span>
          {/* The menu's checkbox has no indeterminate look, so a part-ticked branch says so in text. */}
          {state === 'indeterminate' ? (
            <span className="text-muted-foreground ml-1 text-xs">(some)</span>
          ) : null}
        </DropdownMenuCheckboxItem>,
        ...rows(node.children, depth + 1)
      ];
    });
  }

  const items = rows(tree.roots, 0);

  return (
    <div className="space-y-1">
      <Input
        value={search}
        placeholder="Search risks..."
        className="h-7"
        onChange={(event) => {
          setSearch(event.target.value);
        }}
        onKeyDown={stopMenuTypeahead}
      />
      {items.length === 0 ? (
        <p className="text-muted-foreground px-1.5 py-1 text-xs">No matches.</p>
      ) : (
        <div className="max-h-72 overflow-y-auto">{items}</div>
      )}
    </div>
  );
}

// Tailwind needs whole class names, so the indent per tree level is a lookup rather than a computed value.
const INDENT_CLASSES = ['pl-2', 'pl-5', 'pl-8', 'pl-11', 'pl-14'];

const TRI_STATE_OPTIONS: Array<{key: string; label: string; next: boolean | undefined}> = [
  {key: 'any', label: 'Any', next: undefined},
  {key: 'yes', label: 'Yes', next: true},
  {key: 'no', label: 'No', next: false}
];

function TriStateFilterBody({
  value,
  onChange
}: {
  readonly value: boolean | undefined;
  readonly onChange: (next: boolean | undefined) => void;
}) {
  const current = TRI_STATE_OPTIONS.find((option) => option.next === value)?.key ?? 'any';

  return (
    <ToggleGroup
      variant="outline"
      spacing={0}
      size="sm"
      className="w-full *:flex-1"
      value={[current]}
      onValueChange={(next) => {
        const picked = TRI_STATE_OPTIONS.find((option) => option.key === next[0]);
        if (picked) {
          onChange(picked.next);
        }
      }}>
      {TRI_STATE_OPTIONS.map((option) => (
        <ToggleGroupItem key={option.key} value={option.key}>
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

// Presets a picker exposes; "custom" is always reachable too but isn't one of these - it's
// whatever matches neither preset, or was explicitly chosen to type an exact range into.
const DATE_RANGE_PRESETS = [
  {key: 'last_1_day', label: 'Last 24 hours', days: 1},
  {key: 'last_7_days', label: 'Last 7 days', days: 7},
  {key: 'last_30_days', label: 'Last 30 days', days: 30}
] as const;

type DateRangeKey = (typeof DATE_RANGE_PRESETS)[number]['key'] | 'custom';

// DateTo is only ever set by typing an exact end date - no preset button produces one (they all
// mean "from N days ago through now") - so any dateTo at all means this is a custom range,
// whatever dateFrom happens to equal.
function matchingDateRangeKey(dateFrom: string, dateTo: string): DateRangeKey {
  if (dateTo !== '') {
    return 'custom';
  }

  return (
    DATE_RANGE_PRESETS.find((preset) => dateFrom === isoDateDaysAgo(preset.days))?.key ?? 'custom'
  );
}

// The one label both the picker's own trigger and the workspace's removable date chip use, so
// they never describe the same dateFrom/dateTo pair two different ways.
export function describeDateRange(dateFrom: string, dateTo: string): string {
  const preset = DATE_RANGE_PRESETS.find((p) => p.key === matchingDateRangeKey(dateFrom, dateTo));
  if (preset) {
    return preset.label;
  }

  if (!dateFrom && !dateTo) {
    return 'All time';
  }

  return dateFrom && dateTo
    ? `${dateFrom} → ${dateTo}`
    : `${dateFrom ? 'From' : 'Until'} ${dateFrom || dateTo}`;
}

// One trigger replacing the two always-visible date inputs - a custom range is still one click
// away (the "Custom range" option reveals the same two inputs), it just isn't the default sight.
export function DateRangeFilter({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange
}: {
  readonly dateFrom: string;
  readonly dateTo: string;
  readonly onDateFromChange: (next: string) => void;
  readonly onDateToChange: (next: string) => void;
}) {
  const [mode, setMode] = useState<DateRangeKey>(() => matchingDateRangeKey(dateFrom, dateTo));

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) {
          // Reflects whatever's actually filtering right now, not whichever panel this was left
          // showing the last time it was open.
          setMode(matchingDateRangeKey(dateFrom, dateTo));
        }
      }}>
      <DropdownMenuTrigger
        render={<FilterChip icon={<Calendar />} label={describeDateRange(dateFrom, dateTo)} />}
      />
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuRadioGroup value={mode}>
          {DATE_RANGE_PRESETS.map((preset) => (
            <DropdownMenuRadioItem
              key={preset.key}
              closeOnClick
              value={preset.key}
              onClick={() => {
                setMode(preset.key);
                onDateFromChange(isoDateDaysAgo(preset.days));
                onDateToChange('');
              }}>
              {preset.label}
            </DropdownMenuRadioItem>
          ))}
          <DropdownMenuRadioItem
            value="custom"
            onClick={() => {
              setMode('custom');
            }}>
            Custom range
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        {mode === 'custom' ? (
          <>
            <DropdownMenuSeparator />
            <div className="flex items-center gap-1.5 p-2">
              <Input
                type="date"
                aria-label="From date"
                value={dateFrom}
                className="h-7 min-w-0 flex-1"
                onKeyDown={stopMenuTypeahead}
                onChange={(event) => {
                  onDateFromChange(event.target.value);
                }}
              />
              <span className="text-muted-foreground text-xs">to</span>
              <Input
                type="date"
                aria-label="To date"
                value={dateTo}
                className="h-7 min-w-0 flex-1"
                onKeyDown={stopMenuTypeahead}
                onChange={(event) => {
                  onDateToChange(event.target.value);
                }}
              />
            </div>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Its own toolbar control rather than a section of the Filters popover: status is the first thing
// an analyst narrows by, so it is one click from the grid rather than two. Reads "All Delivery
// Statuses" with nothing picked, which is also its accessible name.
function StatusFilter({
  selected,
  onChange
}: {
  readonly selected: string[];
  readonly onChange: (next: string[]) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <FilterChip
            label={selected.length === 0 ? 'All Delivery Statuses' : 'Delivery Status'}
            count={selected.length}
          />
        }
      />
      <DropdownMenuContent align="start" className="w-56">
        <MultiSelectFilterBody
          label="Delivery Statuses"
          options={STATUS_OPTIONS}
          selected={selected}
          onChange={onChange}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Its own toolbar control rather than a section of the Filters popover: flags exist to be filtered
// on, so it is one click from the grid rather than two.
function FlagFilter({
  selected,
  onChange
}: {
  readonly selected: string[];
  readonly onChange: (next: string[]) => void;
}) {
  const labelOf = useFlagLabel();
  function toggle(value: string) {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  }

  const selectedColors = FLAG_COLORS.filter((color) => selected.includes(color));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <FilterChip
            icon={
              selectedColors.length > 0 ? (
                <span className="flex items-center -space-x-1">
                  {selectedColors.map((color) => (
                    <FlagIcon key={color} color={color} />
                  ))}
                </span>
              ) : (
                <Flag />
              )
            }
            label="Flags"
            count={selected.length}
          />
        }
      />
      <DropdownMenuContent align="start" className="w-48">
        {FLAG_COLORS.map((color) => (
          <DropdownMenuCheckboxItem
            key={color}
            checked={selected.includes(color)}
            onCheckedChange={() => {
              toggle(color);
            }}>
            <FlagIcon color={color} />
            {labelOf(color)} flag
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={selected.includes('none')}
          onCheckedChange={() => {
            toggle('none');
          }}>
          <FlagOff className="text-muted-foreground" />
          Unflagged
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        <MenuClearButton
          disabled={selected.length === 0}
          onClear={() => {
            onChange([]);
          }}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type FiltersPopoverProps = {
  readonly submissionTypeFilter: string[];
  readonly onSubmissionTypeChange: (next: string[]) => void;
  readonly analystFilter: string[];
  readonly analystOptions: MultiSelectOption[];
  readonly onAnalystChange: (next: string[]) => void;
  readonly languageFilter: string[];
  readonly languageOptions: MultiSelectOption[];
  readonly languageSearching: boolean;
  readonly onLanguageChange: (next: string[]) => void;
  readonly onLanguageSearchChange: (term: string) => void;
  readonly topicFilter: string[];
  readonly topicOptions: MultiSelectOption[];
  readonly onTopicChange: (next: string[]) => void;
  readonly locationFilter: string[];
  readonly locationOptions: MultiSelectOption[];
  readonly onLocationChange: (next: string[]) => void;
  readonly riskFilter: string[];
  readonly riskTree: RiskTree;
  readonly onRiskChange: (next: string[]) => void;
  readonly platformStatusFilter: string[];
  readonly onPlatformStatusChange: (next: string[]) => void;
  readonly customerFeedbackFilter: string[];
  readonly onCustomerFeedbackChange: (next: string[]) => void;
  readonly hasCalibrationFilter: boolean | undefined;
  readonly onHasCalibrationChange: (next: boolean | undefined) => void;
  readonly hasCalibrationNotesFilter: boolean | undefined;
  readonly onHasCalibrationNotesChange: (next: boolean | undefined) => void;
  readonly hasReviewerCommentsFilter: boolean | undefined;
  readonly onHasReviewerCommentsChange: (next: boolean | undefined) => void;
  readonly thumbnailChangedFilter: boolean | undefined;
  readonly onThumbnailChangedChange: (next: boolean | undefined) => void;
};

type FilterSectionKey =
  | 'type'
  | 'analyst'
  | 'language'
  | 'topic'
  | 'location'
  | 'risk'
  | 'platform_status'
  | 'customer_feedback'
  | 'calibrated'
  | 'calibration_notes'
  | 'reviewer_comments'
  | 'thumbnail_changed';

// Everything besides Status, Flags and the date range lives here, one popover rather than a dozen
// separate dropdowns crowding the toolbar - matching the Filters button on the Investigations page.
//
// One section's checklist shows at a time (picked via the small tab row), rather than all of them
// stacked with a scrollbar of their own inside an outer scrollbar - that nested-scroll layout was
// hard to use (which list is which, which scrollbar am I even in), and the outer scroll also hid
// which sections existed below the fold until you'd already scrolled past them.
function FiltersPopover(props: FiltersPopoverProps) {
  const sections: Array<{key: FilterSectionKey; label: string; count: number}> = [
    {key: 'type', label: 'Type', count: props.submissionTypeFilter.length},
    {key: 'analyst', label: 'Analyst', count: props.analystFilter.length},
    {key: 'language', label: 'Language', count: props.languageFilter.length},
    {key: 'topic', label: 'Topic', count: props.topicFilter.length},
    {key: 'location', label: 'Location', count: props.locationFilter.length},
    {key: 'risk', label: 'PV / Risk', count: props.riskFilter.length},
    {key: 'platform_status', label: 'Status Check', count: props.platformStatusFilter.length},
    {
      key: 'customer_feedback',
      label: 'Customer Feedback',
      count: props.customerFeedbackFilter.length
    },
    {
      key: 'calibrated',
      label: 'Calibrated',
      count: props.hasCalibrationFilter === undefined ? 0 : 1
    },
    {
      key: 'calibration_notes',
      label: 'Calibration Notes',
      count: props.hasCalibrationNotesFilter === undefined ? 0 : 1
    },
    {
      key: 'reviewer_comments',
      label: 'Reviewer Comments',
      count: props.hasReviewerCommentsFilter === undefined ? 0 : 1
    },
    {
      key: 'thumbnail_changed',
      label: 'Thumbnail Changed',
      count: props.thumbnailChangedFilter === undefined ? 0 : 1
    }
  ];
  const activeCount = sections.reduce((total, section) => total + section.count, 0);
  const [activeSection, setActiveSection] = useState<FilterSectionKey>('type');

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) {
          props.onLanguageSearchChange('');
        }
      }}>
      <DropdownMenuTrigger
        render={<FilterChip icon={<SlidersHorizontal />} label="Filters" count={activeCount} />}
      />
      <DropdownMenuContent align="start" className="w-80 p-2">
        {/* A single-select toggle group rather than a strip of hand-toggled buttons: the pressed
            section is the component's own state (3.0 inverts it to a solid fill), so it no longer
            needs a bg-muted strip behind it to be told apart from the rest. The grid replaces the
            group's one-line row because eleven sections do not fit across the popover. */}
        <ToggleGroup
          size="sm"
          spacing={1}
          aria-label="Filter section"
          className="grid w-full grid-cols-3"
          value={[activeSection]}
          onValueChange={(next) => {
            // Pressing the active section again yields an empty value; one section is always open.
            const [key] = next as FilterSectionKey[];
            if (key) {
              setActiveSection(key);
            }
          }}>
          {/* Three columns truncate the longer names ("Customer Fee…"), so each carries its full
              name in a tooltip. */}
          {sections.map((section) => (
            <Tooltip key={section.key}>
              <TooltipTrigger
                render={<ToggleGroupItem value={section.key} className="min-w-0 px-1" />}>
                <span className="truncate">{section.label}</span>
                {section.count > 0 ? (
                  <Badge className="h-3.5 min-w-3.5 px-0.5">{section.count}</Badge>
                ) : null}
              </TooltipTrigger>
              <TooltipContent side="top">{section.label}</TooltipContent>
            </Tooltip>
          ))}
        </ToggleGroup>

        <div className="mt-1.5">
          {activeSection === 'type' ? (
            <MultiSelectFilterBody
              label="Types"
              options={TYPE_OPTIONS}
              selected={props.submissionTypeFilter}
              onChange={props.onSubmissionTypeChange}
            />
          ) : null}
          {activeSection === 'analyst' ? (
            <MultiSelectFilterBody
              label="Analysts"
              options={props.analystOptions}
              selected={props.analystFilter}
              onChange={props.onAnalystChange}
            />
          ) : null}
          {activeSection === 'language' ? (
            <MultiSelectFilterBody
              label="Languages"
              options={props.languageOptions}
              selected={props.languageFilter}
              emptyLabel={props.languageSearching ? 'Searching…' : 'No matches.'}
              onChange={props.onLanguageChange}
              onSearchChange={props.onLanguageSearchChange}
            />
          ) : null}
          {activeSection === 'topic' ? (
            <MultiSelectFilterBody
              label="Topics"
              options={props.topicOptions}
              selected={props.topicFilter}
              onChange={props.onTopicChange}
            />
          ) : null}
          {activeSection === 'location' ? (
            <MultiSelectFilterBody
              label="Locations"
              options={props.locationOptions}
              selected={props.locationFilter}
              onChange={props.onLocationChange}
            />
          ) : null}
          {activeSection === 'risk' ? (
            <RiskTreeFilterBody
              tree={props.riskTree}
              selected={props.riskFilter}
              onChange={props.onRiskChange}
            />
          ) : null}
          {activeSection === 'platform_status' ? (
            <MultiSelectFilterBody
              label="Status Check"
              options={PLATFORM_STATUS_OPTIONS}
              selected={props.platformStatusFilter}
              onChange={props.onPlatformStatusChange}
            />
          ) : null}
          {activeSection === 'customer_feedback' ? (
            <MultiSelectFilterBody
              label="Customer Feedback"
              options={CUSTOMER_FEEDBACK_OPTIONS}
              selected={props.customerFeedbackFilter}
              onChange={props.onCustomerFeedbackChange}
            />
          ) : null}
          {activeSection === 'calibrated' ? (
            <TriStateFilterBody
              value={props.hasCalibrationFilter}
              onChange={props.onHasCalibrationChange}
            />
          ) : null}
          {activeSection === 'calibration_notes' ? (
            <TriStateFilterBody
              value={props.hasCalibrationNotesFilter}
              onChange={props.onHasCalibrationNotesChange}
            />
          ) : null}
          {activeSection === 'reviewer_comments' ? (
            <TriStateFilterBody
              value={props.hasReviewerCommentsFilter}
              onChange={props.onHasReviewerCommentsChange}
            />
          ) : null}
          {activeSection === 'thumbnail_changed' ? (
            <TriStateFilterBody
              value={props.thumbnailChangedFilter}
              onChange={props.onThumbnailChangedChange}
            />
          ) : null}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type SubmissionFiltersProps = {
  readonly query: string;
  readonly onQueryChange: (next: string) => void;
  readonly statusFilter: string[];
  readonly onStatusChange: (next: string[]) => void;
  readonly dateFrom: string;
  readonly onDateFromChange: (next: string) => void;
  readonly dateTo: string;
  readonly onDateToChange: (next: string) => void;
  readonly flagFilter: string[];
  readonly onFlagChange: (next: string[]) => void;
} & FiltersPopoverProps;

export function SubmissionFilters(props: SubmissionFiltersProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <InputGroup className="w-56 max-w-full">
        <InputGroupAddon>
          <Search />
        </InputGroupAddon>
        <InputGroupInput
          aria-label="Search submissions"
          value={props.query}
          placeholder="Search by handle, name, risk level..."
          onChange={(event) => {
            props.onQueryChange(event.target.value);
          }}
        />
      </InputGroup>

      <StatusFilter selected={props.statusFilter} onChange={props.onStatusChange} />

      <DateRangeFilter
        dateFrom={props.dateFrom}
        dateTo={props.dateTo}
        onDateFromChange={props.onDateFromChange}
        onDateToChange={props.onDateToChange}
      />

      <FlagFilter selected={props.flagFilter} onChange={props.onFlagChange} />

      <FiltersPopover {...props} />
    </div>
  );
}
